/**
 * Public landing for a Trusted Circle invite link (/invite/:token).
 *
 * Flow:
 *   1. Fetch the preview (no auth required) → show family + role + perms.
 *   2. If signed in → "Accept" button consumes the token.
 *   3. If not signed in → bounce to /login with `?next=/invite/:token` so the
 *      user lands back here after auth and can accept.
 */
import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ShieldCheck, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  PERMISSION_COPY,
  ROLE_COPY,
  circleApi,
  type CirclePermission,
} from '@/api/circle';
import { getAccessToken } from './tokenStore';

export function AcceptInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => circleApi.previewInvitation(token),
    retry: false,
    enabled: Boolean(token),
  });

  const accept = useMutation({
    mutationFn: () => circleApi.acceptInvitation(token),
    onSuccess: () => {
      toast.success('Welcome to the family.');
      navigate('/', { replace: true });
    },
    onError: (e: Error) => {
      if (e.message.includes('already_in_other_family')) {
        toast.error('You’re already in a family — leave it first.');
      } else if (e.message.includes('already_in_family')) {
        toast.message('You’re already in this family.');
        navigate('/', { replace: true });
      } else {
        toast.error('Could not accept invitation.');
      }
    },
  });

  const decline = useMutation({
    mutationFn: () => circleApi.declineInvitation(token),
    onSuccess: () => {
      toast.success('Invitation declined.');
      navigate('/login', { replace: true });
    },
  });

  const signedIn = getAccessToken() !== null;

  // If the link is opened by someone not signed in, send them to login with
  // a redirect-back so we return here after auth and they can accept.
  useEffect(() => {
    if (!signedIn && preview.isSuccess) {
      const next = encodeURIComponent(location.pathname);
      const t = setTimeout(() => navigate(`/login?next=${next}`, { replace: true }), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [signedIn, preview.isSuccess, location.pathname, navigate]);

  return (
    <main className="grid min-h-screen place-items-center bg-[color:var(--kz-color-surface-sunken)] px-6 py-12">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="mb-2 grid size-10 place-items-center rounded-full bg-[color:var(--kz-color-brand-primary)]/10">
            <ShieldCheck className="size-5 text-[color:var(--kz-color-brand-primary)]" />
          </div>
          <CardTitle>Trusted Circle invitation</CardTitle>
          <CardDescription>
            {preview.isPending && 'Loading invitation…'}
            {preview.isError && 'This link is invalid, expired, or already used.'}
            {preview.data && (
              <>
                You’ve been invited to join the <strong>{preview.data.family_name}</strong> family
                as <strong>{ROLE_COPY[preview.data.role].label}</strong>.
              </>
            )}
          </CardDescription>
        </CardHeader>

        {preview.data && (
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
                What you’ll be able to do
              </p>
              <ul className="mt-2 grid gap-2">
                {(preview.data.permissions as CirclePermission[]).map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                    <div>
                      <p className="font-medium">{PERMISSION_COPY[p].label}</p>
                      <p className="text-xs text-[color:var(--kz-color-ink-muted)]">{PERMISSION_COPY[p].hint}</p>
                    </div>
                  </li>
                ))}
                {preview.data.permissions.length === 0 && (
                  <li className="text-sm italic text-[color:var(--kz-color-ink-muted)]">
                    No permissions selected. Ask the inviter to add some before you accept.
                  </li>
                )}
              </ul>
            </div>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
              Expires {new Date(preview.data.expires_at).toLocaleString()}.
            </p>
          </CardContent>
        )}

        <CardFooter className="flex flex-wrap items-center justify-end gap-2">
          {preview.isError && (
            <Button asChild variant="outline">
              <Link to="/login">Go to login</Link>
            </Button>
          )}
          {preview.data && signedIn && (
            <>
              <Button
                variant="ghost"
                onClick={() => decline.mutate()}
                disabled={decline.isPending || accept.isPending}
              >
                <X className="size-4" /> Decline
              </Button>
              <Button
                onClick={() => accept.mutate()}
                disabled={accept.isPending || preview.data.permissions.length === 0}
              >
                {accept.isPending ? 'Joining…' : 'Accept invitation'}
              </Button>
            </>
          )}
          {preview.data && !signedIn && (
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
              Redirecting to sign-in…
            </p>
          )}
        </CardFooter>
      </Card>
    </main>
  );
}
